import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";

// Static label mapping for known top-level path segments.
// Dynamic segments (ids/slugs) are resolved via BreadcrumbContext, with a
// title-cased fallback derived from the URL slug itself.
const STATIC_LABELS = {
    courses: "Courses",
    "course-categories": "Courses",
    products: "Products",
    account: "My Account",
    membership: "Membership",
    about: "About Us",
    "reset-password": "Reset Password",
};

// Some segments are conceptually grouped under another route (e.g. a course
// category slug page belongs under /courses). When rendering an intermediate
// crumb for these segments we point the link at the canonical destination.
const PATH_REMAP = {
    "/course-categories": "/courses",
};

// Routes where breadcrumbs should be suppressed entirely.
const HIDDEN_PREFIXES = ["/admin"];

function prettify(segment) {
    if (!segment) return "";
    // Skip opaque IDs (UUIDs, hex strings, mongo-ish) — these need a dynamic label
    // from the page to be meaningful; otherwise we omit the crumb entirely.
    if (/^[0-9a-f]{8}-?[0-9a-f-]{4,}$/i.test(segment)) return "";
    if (/^[0-9a-f]{20,}$/i.test(segment)) return "";
    return segment
        .split("-")
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
        .join(" ");
}

export default function Breadcrumbs() {
    const { pathname } = useLocation();
    const { labels } = useBreadcrumbs();

    if (pathname === "/" || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) {
        return null;
    }

    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 0) return null;

    // Build cumulative crumbs.
    const crumbs = [];
    let acc = "";
    segments.forEach((seg, idx) => {
        acc += "/" + seg;
        // Prefer dynamic label set by the active page, else static map, else prettified slug.
        const dyn = labels[acc];
        const staticLabel = STATIC_LABELS[seg];
        const label = dyn || staticLabel || prettify(seg);
        if (!label) return; // Skip opaque ids when no friendly label is available.
        crumbs.push({ to: PATH_REMAP[acc] || acc, label, isLast: idx === segments.length - 1 });
    });

    // Ensure the very last crumb is non-clickable even if its `to` is set, by
    // recomputing isLast based on filtered crumbs.
    if (crumbs.length === 0) return null;
    crumbs[crumbs.length - 1].isLast = true;

    return (
        <nav
            aria-label="Breadcrumb"
            className="border-b border-gray-200 bg-white"
            data-testid="breadcrumbs-nav"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ol className="flex items-center gap-1.5 py-2.5 text-xs sm:text-sm overflow-x-auto whitespace-nowrap">
                    <li className="flex items-center">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-1 text-ink-500 hover:text-brand-600 transition-colors"
                            data-testid="breadcrumb-home"
                        >
                            <Home className="h-3.5 w-3.5" />
                            <span className="sr-only sm:not-sr-only">Home</span>
                        </Link>
                    </li>
                    {crumbs.map((c, i) => (
                        <li key={`${c.to}-${i}`} className="flex items-center gap-1.5">
                            <ChevronRight className="h-3.5 w-3.5 text-ink-400 shrink-0" aria-hidden />
                            {c.isLast ? (
                                <span
                                    className="font-medium text-ink-900 truncate max-w-[60vw] sm:max-w-xs"
                                    aria-current="page"
                                    data-testid={`breadcrumb-current`}
                                >
                                    {c.label}
                                </span>
                            ) : (
                                <Link
                                    to={c.to}
                                    className="text-ink-500 hover:text-brand-600 transition-colors truncate max-w-[40vw] sm:max-w-xs"
                                    data-testid={`breadcrumb-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {c.label}
                                </Link>
                            )}
                        </li>
                    ))}
                </ol>
            </div>
        </nav>
    );
}
